import { MeshArea } from '@imaaagery/a3camera';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { StyledComponent } from 'styled-components';
import { LAYERS_PER_CONCERN } from '../constantes/layersPerConcern';
import { getConcernLevel } from '../constantes/utilitaries';
import { ConcernCanvas } from '../styles/concernImageStyle';
import { ConcernImgSkeleton } from '../styles/resultStyle';
import { CliniqueState, Layer } from '../type';

interface ConcernImageProps {
	concern: string;
	image: string;
	as?: StyledComponent<'canvas', any>;
}

const LAYER_REFERENCE_WIDTH = 768;
const WRINKLES_VISUALIZATION_MIN_SCORE = 0.2;
const DEMKY_ANALYSIS_IMAGE_SIZE = 2048;
const WRINKLES_VISUALIZATION_MIN_RADIUS = 2;
const scaleLayerOffset = (value: number | undefined, canvasWidth: number) => (value ?? 0) * (canvasWidth / LAYER_REFERENCE_WIDTH);
const getLayerAnchorPoint = (annotations: any, anchor: { name: string; index: number }, width: number, height: number) => ({
	x: annotations[anchor.name as MeshArea][anchor.index][0] * width,
	y: annotations[anchor.name as MeshArea][anchor.index][1] * height,
});

const getLayerDrawBox = (layer: Layer, annotations: any, width: number, height: number, zoom: number) => {
	if (layer.placement) {
		const center = 'between' in layer.placement.center
			? layer.placement.center.between
				.map((anchor) => getLayerAnchorPoint(annotations, anchor, width, height))
				.reduce((acc, point) => ({ x: acc.x + point.x / 2, y: acc.y + point.y / 2 }), { x: 0, y: 0 })
			: getLayerAnchorPoint(annotations, layer.placement.center, width, height);
		const centerX = center.x + scaleLayerOffset(layer.placement.center.offsetX, width);
		const centerY = center.y + scaleLayerOffset(layer.placement.center.offsetY, width);
		const layerWidth = scaleLayerOffset(layer.placement.size.width, width) * zoom;
		const layerHeight = scaleLayerOffset(layer.placement.size.height, width) * zoom;
		return {
			x: centerX - layerWidth / 2,
			y: centerY - layerHeight / 2,
			width: layerWidth,
			height: layerHeight,
			xMirrored: layer.placement.xMirrored,
			yMirrored: layer.placement.yMirrored,
		};
	}

	if (!layer.position) return null;

	const x1 = annotations[layer.position.topLeft.name as MeshArea][layer.position.topLeft.index][0] * width + scaleLayerOffset(layer.position.topLeft.offsetX, width);
	const y1 = annotations[layer.position.topLeft.name as MeshArea][layer.position.topLeft.index][1] * height + scaleLayerOffset(layer.position.topLeft.offsetY, width);
	const x2 = annotations[layer.position.bottomRight.name as MeshArea][layer.position.bottomRight.index][0] * width + scaleLayerOffset(layer.position.bottomRight.offsetX, width);
	const y2 = annotations[layer.position.bottomRight.name as MeshArea][layer.position.bottomRight.index][1] * height + scaleLayerOffset(layer.position.bottomRight.offsetY, width);

	return {
		x: x1 + scaleLayerOffset(layer.position.offsetX, width),
		y: y1 + scaleLayerOffset(layer.position.offsetY, width),
		width: (x2 - x1) * zoom,
		height: (y2 - y1) * zoom,
		xMirrored: layer.position.xMirrored,
		yMirrored: layer.position.yMirrored,
	};
};

const getDemkyFaceBox = (canvasWidth: number, canvasHeight: number, demkyPixels: CliniqueState['session']['demkyInvisibleDamage']) => {
	const faceBoundingBox = demkyPixels?.faceBoundingBox;

	return faceBoundingBox
		? {
			x: faceBoundingBox.x * canvasWidth,
			y: faceBoundingBox.y * canvasHeight,
			width: faceBoundingBox.width * canvasWidth,
			height: faceBoundingBox.height * canvasHeight,
		}
		: {
			x: 0,
			y: 0,
			width: canvasWidth,
			height: canvasHeight,
		};
};

const getDemkyPixelStats = (demkyPixels: CliniqueState['session']['demkyInvisibleDamage']) => {
	if (!demkyPixels?.detectedPixels?.length) return null;

	return demkyPixels.detectedPixels.reduce(
		(acc, pixel) => ({
			minX: Math.min(acc.minX, pixel.x),
			maxX: Math.max(acc.maxX, pixel.x),
			minY: Math.min(acc.minY, pixel.y),
			maxY: Math.max(acc.maxY, pixel.y),
			arePixelsNormalized: acc.arePixelsNormalized && pixel.x >= 0 && pixel.x <= 1 && pixel.y >= 0 && pixel.y <= 1,
		}),
		{
			minX: demkyPixels.detectedPixels[0].x,
			maxX: demkyPixels.detectedPixels[0].x,
			minY: demkyPixels.detectedPixels[0].y,
			maxY: demkyPixels.detectedPixels[0].y,
			arePixelsNormalized: true,
		},
	);
};

const getDemkySourcePoint = (squareX: number, squareY: number, box: { width: number; height: number }) => {
	const scaledCropWidthRatio = box.width / Math.max(box.height, 1);
	const horizontalOffsetRatio = (1 - scaledCropWidthRatio) / 2;

	return {
		x: (squareX - horizontalOffsetRatio) / scaledCropWidthRatio,
		y: squareY,
	};
};

const drawDemkyBrush = (imageData: ImageData, canvasWidth: number, canvasHeight: number, x: number, y: number, radius: number) => {
	for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
		for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
			if (offsetX * offsetX + offsetY * offsetY > radius * radius) continue;
			const drawX = x + offsetX;
			const drawY = y + offsetY;
			if (drawX < 0 || drawX >= canvasWidth || drawY < 0 || drawY >= canvasHeight) continue;

			const index = (drawY * canvasWidth + drawX) * 4;
			imageData.data[index] = 255;
			imageData.data[index + 1] = 255;
			imageData.data[index + 2] = 255;
			imageData.data[index + 3] = 255;
		}
	}
};

const drawDemkyPixels = (imageData: ImageData, canvasWidth: number, canvasHeight: number, demkyPixels: CliniqueState['session']['demkyInvisibleDamage'], concern: string) => {
	if (!demkyPixels?.detectedPixels?.length) return;

	const stats = getDemkyPixelStats(demkyPixels);
	if (!stats) return;

	const box = getDemkyFaceBox(canvasWidth, canvasHeight, demkyPixels);
	const brushRadius = concern === 'lines_wrinkles'
		? Math.max(WRINKLES_VISUALIZATION_MIN_RADIUS, Math.round(canvasWidth * 0.0015))
		: 0;

	demkyPixels.detectedPixels.forEach((pixel) => {
		const squareX = stats.arePixelsNormalized ? pixel.x : pixel.x / DEMKY_ANALYSIS_IMAGE_SIZE;
		const squareY = stats.arePixelsNormalized ? pixel.y : pixel.y / DEMKY_ANALYSIS_IMAGE_SIZE;
		const sourcePoint = getDemkySourcePoint(squareX, squareY, box);
		if (sourcePoint.x < 0 || sourcePoint.x > 1 || sourcePoint.y < 0 || sourcePoint.y > 1) return;

		const x = Math.round(box.x + sourcePoint.x * box.width);
		const y = Math.round(box.y + sourcePoint.y * box.height);
		drawDemkyBrush(imageData, canvasWidth, canvasHeight, x, y, brushRadius);
	});
};

const ConcernImage = ({ concern, as }: ConcernImageProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const { imageUser, predictions, analyses, demkyInvisibleDamage, demkyWrinklesVisualization } = useSelector((state: CliniqueState) => state.session);
	const [loading, setLoading] = useState<boolean>(false);
	const useLayers = Object.values(LAYERS_PER_CONCERN[concern] ?? {}).some((layers) => layers.length > 0);
	const useUserImageOnly = concern === 'lines_wrinkles' || concern === 'blemishes';

	const score = analyses.find((a) => a.name === concern)?.score || 0;
	const canShowWrinklesVisualization = concern === 'lines_wrinkles' && score >= WRINKLES_VISUALIZATION_MIN_SCORE && !!demkyWrinklesVisualization?.detectedPixels?.length;
	const hasRenderableSource = (useLayers || useUserImageOnly || concern === 'invisible_damages') && !!imageUser;

	// User default image with layers
	useEffect(() => {
		if (!useLayers) return;
		if (!canvasRef.current) return;
		if (!imageUser) return;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;
		setLoading(true);
		const img = new Image();
		img.src = imageUser;
		img.onload = () => {
			canvas.width = img.width;
			canvas.height = img.height;
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(img, 0, 0);
			ctx.strokeStyle = '#fff';
			ctx.fillStyle = '#fff';
			ctx.lineWidth = 4;
			setLoading(false);

			let pred = analyses.find((analyse) => analyse.name === concern)?.prediction;
			if (pred) {
				// New part for lines and wrinkles
				pred.forEach((p: any) => {
					const pillarboxWidth = (canvas.height - canvas.width) / 2;

					switch (p.class) {
						case 'acne':
							const { topLeft, bottomRight } = p.normalized_mask.reduce(
								(acc: any, point: any) => {
									if (point[0] < acc.topLeft[0]) acc.topLeft[0] = point[0];
									if (point[0] > acc.bottomRight[0]) acc.bottomRight[0] = point[0];
									if (point[1] < acc.topLeft[1]) acc.topLeft[1] = point[1];
									if (point[1] > acc.bottomRight[1]) acc.bottomRight[1] = point[1];
									return acc;
								},
								{ topLeft: [p.normalized_mask[0][0], p.normalized_mask[0][1]], bottomRight: [p.normalized_mask[0][0], p.normalized_mask[0][1]] },
							);
							const center = [(topLeft[0] + bottomRight[0]) / 2, (topLeft[1] + bottomRight[1]) / 2];
							const radius = Math.max(bottomRight[0] - topLeft[0], bottomRight[1] - topLeft[1]) * 1.4;
							ctx.beginPath();
							ctx.arc(center[0] * canvas.height - pillarboxWidth, center[1] * canvas.height, radius * canvas.height, 0, 2 * Math.PI);
							ctx.stroke();
							break;
						default:
							ctx.beginPath();
							ctx.moveTo(p.normalized_mask[0][0] * canvas.height - pillarboxWidth, p.normalized_mask[0][1] * canvas.height);
							p.normalized_mask.forEach((m: number[]) => {
								ctx.lineTo(m[0] * canvas.height - pillarboxWidth, m[1] * canvas.height);
							});
							ctx.closePath();
							ctx.stroke();
					}
				});
			}

			// Layers
			if (!predictions) return;

			const silhouette = predictions.annotations.silhouette;
			const { lowestX, highestX } = silhouette.reduce(
				(acc: any, point: any) => {
					const pointX = point[0] * img.width;
					if (pointX < acc.lowestX) acc.lowestX = pointX;
					if (pointX > acc.highestX) acc.highestX = pointX;
					return acc;
				},
				{ lowestX: silhouette[0][0] * img.width, highestX: silhouette[0][0] * img.width },
			);

			const faceWidth = highestX - lowestX;
			const faceWidthRatio = faceWidth / img.width;

			const annotations = predictions.annotations;
			const level = getConcernLevel(score);
			(LAYERS_PER_CONCERN[concern]?.[level] || []).forEach((layer) => {
				const layerImg = new Image();
				layerImg.src = layer.image;

				layerImg.onload = () => {
					const imgWidth = canvas.width;
					const imgHeight = canvas.height;
					const zoom = (layer.zoom || 1) * (1 - (0.5 - faceWidthRatio));
					const drawBox = getLayerDrawBox(layer, annotations, imgWidth, imgHeight, zoom);
					if (!drawBox) return;

					if (layer.opacity) {
						ctx.globalAlpha = layer.opacity;
					}

					const hRatio = drawBox.width / layerImg.width;
					const vRatio = drawBox.height / layerImg.height;
					const ratio = Math.max(hRatio, vRatio);
					const centerShift_x = (drawBox.width - layerImg.width * ratio) / 2;
					const centerShift_y = (drawBox.height - layerImg.height * ratio) / 2;
					console.log(`drawing ${layer.image} for concern ${concern}`);

					ctx.drawImage(layerImg, drawBox.x + centerShift_x, drawBox.y + centerShift_y, layerImg.width * ratio * (drawBox.xMirrored ? -1 : 1), layerImg.height * ratio * (drawBox.yMirrored ? -1 : 1));
					ctx.globalAlpha = 1;
				};
			});
		};
		img.onerror = () => setLoading(false);
	}, [analyses, concern, imageUser, predictions, score, useLayers]);

	// User image with Demky visualization pixels
	useEffect(() => {
		if (concern !== 'invisible_damages' && concern !== 'lines_wrinkles') return;
		if (!canvasRef.current) return;
		if (!imageUser) return;
		if (concern === 'lines_wrinkles' && !canShowWrinklesVisualization) return;
		const demkyPixels = concern === 'invisible_damages' ? demkyInvisibleDamage : demkyWrinklesVisualization;
		if (!demkyPixels?.detectedPixels?.length) return;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;
		setLoading(true);

		const sourceImg = new Image();
		sourceImg.src = imageUser;
		sourceImg.onload = () => {
			canvas.width = sourceImg.width;
			canvas.height = sourceImg.height;
			ctx.clearRect(0, 0, canvas.width, canvas.height);
				ctx.drawImage(sourceImg, 0, 0);

				const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
				drawDemkyPixels(imageData, canvas.width, canvas.height, demkyPixels, concern);

				ctx.putImageData(imageData, 0, 0);
			setLoading(false);
		};
		sourceImg.onerror = () => setLoading(false);
	}, [canShowWrinklesVisualization, concern, demkyInvisibleDamage, demkyWrinklesVisualization, imageUser]);

	// User image only
	useEffect(() => {
		if (!useUserImageOnly) return;
		if (canShowWrinklesVisualization) return;
		if (!canvasRef.current) return;
		if (!imageUser) return;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;
		setLoading(true);

		const sourceImg = new Image();
		sourceImg.src = imageUser;
		sourceImg.onload = () => {
			canvas.width = sourceImg.width;
			canvas.height = sourceImg.height;
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(sourceImg, 0, 0);
			setLoading(false);
		};
		sourceImg.onerror = () => setLoading(false);
	}, [canShowWrinklesVisualization, imageUser, useUserImageOnly]);

	return (
		<>
			<ConcernCanvas as={as} ref={canvasRef} style={{ display: loading || !hasRenderableSource ? 'none' : 'block' }} />
			{/* <ConcernRender style={{ display: loading ? 'none' : 'block' }} /> */}
			{(loading || !hasRenderableSource) && <ConcernImgSkeleton as={as} />}
		</>
	);
};

export default ConcernImage;
